import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EmptyState, PageHeader, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, ListBullets } from "@phosphor-icons/react";
import { dialingTypeLabel } from "@/lib/campaignForm";

export default function CampaignLeadsToDial() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/campaigns/${campaignId}/list-dialability`);
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const camp = data?.campaign;
  const statuses = data?.dial_statuses || [];
  const lists = data?.lists || [];
  const totals = data?.totals || {};

  return (
    <div data-testid="campaign-leads-to-dial-page">
      <PageHeader
        title={camp ? `Leads to dial — ${camp.name}` : "Leads to dial"}
        subtitle={
          camp
            ? `${dialingTypeLabel(camp.dialing_type)} · Assigned lists with lead counts for each Dial Status on this campaign.`
            : "Assigned lists and dial-status lead counts."
        }
      >
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="rounded-lg"
            data-testid="leads-to-dial-back"
          >
            <Link to="/campaigns">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Campaigns
            </Link>
          </Button>
          {camp?.id && (
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => navigate(`/campaigns/${camp.id}/edit`)}
              data-testid="leads-to-dial-edit"
            >
              Edit campaign
            </Button>
          )}
        </div>
      </PageHeader>

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data ? (
        <EmptyState
          icon={ListBullets}
          title="Campaign not found"
          description="This campaign may have been deleted."
          testid="leads-to-dial-missing"
        />
      ) : lists.length === 0 ? (
        <EmptyState
          icon={ListBullets}
          title="No lists assigned"
          description="Assign lead lists on the campaign edit page to see dialable counts."
          testid="leads-to-dial-empty-lists"
        />
      ) : statuses.length === 0 ? (
        <EmptyState
          icon={ListBullets}
          title="No dial statuses"
          description="Select Dial Statuses on the campaign so the dialer knows which lead statuses to call."
          testid="leads-to-dial-empty-statuses"
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm" data-testid="leads-to-dial-summary">
            {camp?.status && <StatusPill status={camp.status} />}
            <span>
              <strong className="tabular-nums">{totals.dialable_leads ?? 0}</strong>
              {" "}dialable across{" "}
              <strong className="tabular-nums">{totals.lists ?? lists.length}</strong>
              {" "}list{(totals.lists ?? lists.length) === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground">
              Dial statuses: {statuses.join(", ")}
            </span>
          </div>

          <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="leads-to-dial-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>List</TableHead>
                  {statuses.map((st) => (
                    <TableHead key={st} className="text-right tabular-nums">{st}</TableHead>
                  ))}
                  <TableHead className="text-right">Dialable</TableHead>
                  <TableHead className="text-right">Total leads</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lists.map((row) => (
                  <TableRow key={row.id} data-testid={`leads-to-dial-list-${row.id}`}>
                    <TableCell>
                      <Link
                        to={`/lists/${row.id}`}
                        className="font-medium text-primary hover:underline"
                        data-testid={`leads-to-dial-list-link-${row.id}`}
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    {statuses.map((st) => (
                      <TableCell key={st} className="text-right tabular-nums text-sm">
                        {row.by_status?.[st] ?? 0}
                      </TableCell>
                    ))}
                    <TableCell className={`text-right font-semibold tabular-nums ${
                      (row.dialable_leads || 0) > 0 ? "" : "text-destructive"
                    }`}>
                      {row.dialable_leads ?? 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {row.total_leads ?? 0}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/30 font-medium" data-testid="leads-to-dial-totals">
                  <TableCell>Total</TableCell>
                  {statuses.map((st) => (
                    <TableCell key={st} className="text-right tabular-nums">
                      {totals.by_status?.[st] ?? 0}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums">
                    {totals.dialable_leads ?? 0}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {lists.reduce((s, r) => s + (r.total_leads || 0), 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
